import React, { useReducer, Reducer, useEffect } from 'react'
import styled from 'styled-components'
import { up } from 'styled-breakpoints'
import { useTranslation } from 'react-i18next'

import TextareaAutosize from 'react-textarea-autosize'

import { InputLabel } from '../../input/input'
import Loader from '../../loader'
import Stars from '../../stars'

import { Book, BookWithRating, Review } from '../../../../data/book/data-types'
import { useAuth } from '../../../../data/hooks/auth'
import { DialogLevel, useDialog } from '../../dialog/hook'
import { useReview } from '../../../../data/hooks/reviews'
import { createReview, updateReview } from '../../../../data/book/reviews'

type ReviewDialogProps = {
    book: Book | BookWithRating
}

type Errors = {
    rating: string | null
    title: string | null
    body: string | null
}

const ReviewDialog: React.FunctionComponent<ReviewDialogProps> = ({ book }) => {
    const { user, token } = useAuth()
    const { t } = useTranslation("reviews")
    const { t: bookT } = useTranslation('book')

    const { closeDialog } = useDialog()

    const { pending, review } = useReview((book as BookWithRating)?.rating?.my_review_id)

    const [state, setState] = useReducer<Reducer<Partial<Review>, Partial<Review>>>(
        (oldState: Partial<Review>, newState: Partial<Review>) => ({ ...oldState, ...newState}),
        {
            rating: (book as BookWithRating)?.rating.user || undefined,
            ...(review || {}),
        }
    )

    const [errors, setErrors] = useReducer<Reducer<Errors, Partial<Errors>>>(
        (oldState, newState) => ({ ...oldState, ...newState}),
        {
            rating: null,
            title: null,
            body: null,
        }
    )

    useEffect(() => {
        if (review && review.is_completed) {
            setState({
                title: review.title,
                body: review.body,
                is_spoiler: review.is_spoiler,
            })
        }
    }, [book.id, review?.id])

    useEffect(() => {
        setErrors({
            rating: null,
            title: null,
            body: null,
        })
    }, [user?.id, book.id])

    if (pending) {
        return <Container>
            <div style={{
                width: "100%",
                display: "grid",
                "justifyItems": "center",
            }}>
                <Loader />
            </div>
        </Container>
    }


    const submitReview = async () => {
        setErrors({
            rating: null,
            title: null,
            body: null,
        })

        const newErrors: { [key: string]: string } = {}

        // check everything
        if (!state.rating) {
            newErrors.rating = t('add_review.errors.rating_missing')
        }

        if (state.rating === 0) {
            newErrors.rating = t('add_review.errors.rating_zero')
        }

        if (!state.title || state.title.length < 4) {
            newErrors.title = t('add_review.errors.short_title')
        }

        if (!state.body || state.body.length < 4) {
            newErrors.body = t('add_review.errors.short_body')
        }

        console.log(newErrors)

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        if (state.id) {
            await updateReview(state.id!, state, token?.access!)
        } else {
            await createReview(book.id!, state, token?.access!)
        }

        closeDialog(DialogLevel.Top)
    }

    return (
        <Container>
            <Title> {t("add_review.title")} </Title>
            <BookWrapper>
                <ImageContainer>
                    <Image src={book.image} alt={book.title} />
                    <ISBN>{book.isbn13}</ISBN>
                </ImageContainer>
                <BookTitle>{book.title}</BookTitle>
                <Authors>{bookT("by")} {book.authors?.map(a => a.name).join(", ")}</Authors>
            </BookWrapper>
            <ReviewWrapper>
                <MyRating>
                    <RatingTitle>{t("my_rating")}</RatingTitle>
                    <ReviewStars value={state.rating || 0} onValueChanged={(rating) => setState({ rating })}/>
                    <Error error={errors.rating}>{errors.rating}</Error>
                </MyRating>
                <ReviewInputLabel 
                    placeholder={t("add_review.placeholders.title")} 
                    error={errors.title} 
                    onChange={(ev) => setState({ title: ev.target.value })}
                    value={state.title}
                />
                <AreaContainer error={null}>
                    <TextArea 
                        placeholder={t("add_review.placeholders.body")} 
                        minRows={5}
                        maxRows={5}
                        onChange={(ev) => setState({ body: ev.target.value })}
                        value={state.body}
                    />
                    <Error error={errors.body}>{errors.body}</Error>
                </AreaContainer>
                <SpoilerContainer>
                    <CheckBox 
                        type="checkbox"
                        onChange={() => setState({ is_spoiler: !state.is_spoiler })} 
                        checked={state.is_spoiler || false}
                    />
                    <Label>{t('add_review.placeholders.spoiler')}</Label>
                </SpoilerContainer>
                <Buttons>
                    <Button onClick={() => closeDialog(DialogLevel.Top)} active={false}>{t('add_review.cancel')}</Button>
                    <Button onClick={submitReview} active={true}>{t('add_review.publish')}</Button>
                </Buttons>
            </ReviewWrapper>
        </Container>
    )
}

const Container = styled.div`
    position: relative;
    
    display: grid;
    align-items: start;
    
    grid-template-columns: 1fr;
    grid-row-gap: 24px;
    grid-column-gap: 24px;

    width: 100%;
    max-width: 1024px;

    padding-bottom: 24px;

    background: ${p => p.theme.colors.bg};
    border-radius: 10px;

	${up('tablet')} {
        width: 650px;

        grid-template-columns: auto 1fr;
        grid-column-gap: 48px;
	}
`

const Title = styled.div`
    display: grid;
    align-items: center;
    justify-items: center;

    width: 100%;
    height: 64px;
    
    padding: 10px;

    font-weight: bold;

    border-bottom: 1px solid ${p => p.theme.colors.serviceGray};

    ${up('tablet')} {
		height: 64px;

        grid-column: 1 / span 2;
	}
`

const BookWrapper = styled.div`
    display: grid;
    grid-gap: 16px;
    justify-items: center;
    align-items: center;

    width: 100%;
    padding-bottom: 24px;

    border-bottom: 1px solid ${p => p.theme.colors.serviceGray};

    ${up('tablet')} {
        width: auto;
		margin-left: 48px;

        border-bottom: none;
	}
`

const ImageContainer = styled.div`
    position: relative;
    width: fit-content;
    border-radius: 10px;
    overflow: hidden;
`

const Image = styled.img`
    display: grid;

    width: 136px;
    aspect-ratio: 6 / 9;
`

const BookTitle = styled.div`
    font-weight: bold;
    font-size: 16px;

    ${up('tablet')} {
		font-size: 14px;

        border-bottom: none;
	}
`

const Authors = styled.div`
    font-size: 16px;
    color: ${p => p.theme.colors.darkGray};

    ${up('tablet')} {
		font-size: 14px;

        border-bottom: none;
	}
`

const ISBN = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    background: rgba(0, 0, 0, 0.3);
    color: ${p => p.theme.colors.bg};

    font-weight: bold;
    font-size: 12px;
    line-height: 28px;

    text-align: center;
`

const ReviewWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 24px;

    padding: 0 32px;

    ${up('tablet')} {
        height: 100%;
        margin-right: 48px;

        padding: 0;
	}
`

const ReviewStars = styled(Stars)`
    height: 40px;
`

const MyRating = styled.div`
    position: relative;

    height: fit-content;
    display: grid;
    grid-gap: 16px;
`

const RatingTitle = styled.div`
    font-weight: bold;
`

const ReviewInputLabel = styled(InputLabel)`
    width: 100%;
    height: 40px;

    margin: 0;
`

const TextArea = styled(TextareaAutosize)<{error?: string | null, type?: string }>`
    width: 100%;

	font-size: 14px;
	line-height: 20px;

	::placeholder,
	::-webkit-input-placeholder {
		opacity: 0.5;
	}
	:-ms-input-placeholder {
		opacity: 0.5;
	}

    &, &:active {
		outline: none;
		border: none;
	}
`

const AreaContainer = styled.div<{ error: string | null}>`
    position: relative;
	display: flex;

	padding: 13px 20px;
	margin: 10px 0 30px 0;

	/* All visual input properties should be set on Container so they can be changed from outside with className */
	background: ${p => p.theme.colors.bg};

	border-radius: 8px;
	border: 1px solid ${p => p.error
								? p.theme.colors.error
								: p.theme.colors.lightGray};

	&:focus {
		border: 1px solid ${p => p.error
									? p.theme.colors.error
									: p.theme.colors.accent};
    }

	overflow: visible;
`

const Buttons = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    justify-items: start;

    grid-gap: 20px;
`

const Button = styled.div<{ active?: boolean }>`
    display: grid;
    align-items: center;
    justify-content: center;

    width: 100%;
    height: fit-content;
    padding: 10px 32px;

    font-size: 16px;
    font-weight: 600;
    line-height: 24px;

    background: ${p => p.active ? p.theme.colors.accent : p.theme.colors.bg};

    border: 1px solid ${p => p.active ? p.theme.colors.bg : p.theme.colors.accent};
    border-radius: 10px;
    cursor: pointer;

    &, &:active, &:visited {
        text-decoration: none;
        outline: none;
        color: ${p => p.active ? p.theme.colors.bg : p.theme.colors.accent}
    }
`

const Error = styled.div<{ error: string | null}>`
    display: ${p => p.error ? 'grid' : 'none'};
    position: absolute;
    top: 100%;
    left: 10px;

    padding: 3px 5px;
    font-size: 12px;
    color: ${p => p.theme.colors.error};
`

const SpoilerContainer = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    justify-items: start;
`

const CheckBox = styled.input`
    font-size: 14px;
	line-height: 20px;
`

const Label = styled.div`
    font-size: 14px;
	line-height: 20px;
`

export default ReviewDialog